
/**
 * @author ysxx
 * @email [yishuiwy@163.com]
 * @create date 2021-12-14 16:25:43
 * @modify date 2021-12-14 16:25:43
 * @desc 剧情树
 */
import { FileWriter ,FileReader} from '@/platform'
import ModuleId from './DataStructModuleId'
import MapInfo from './MapInfo'
import type DataMap from './DataMap'
import type EventBase from './EventClass/EventBase'
import { ensureEventListInstances } from './EventClass/eventFromReader'
import { normalizeString } from './gameBinUtils'

/** 与 UE 规格一致：StoryTree 块仅含到 List.length；各场景为独立 mid，经 DataMap 定位读取 */
export type StoryTreeReadContext = {
	dataMap: DataMap
	openModuleReader?: (mid: number) => Promise<FileReader | null>
}

class StoryTree {
	public mid = ModuleId.storyTree
	private mapList: Array<MapInfo> = []

	/**
	 * 工程整体名称
	 */
	public ProjectName: string = ''

	/**
	 * 最近一次选中的剧情节点
	 */
	public SelectMapIndex: number = 0
	
	/**
	 * 好像没用了 project_bin_no
	 */
	public Id: number = 0

	/** 根剧情节点（平铺顺序：Order → Id），与 `Childs` 树一致，供界面直接挂载树形数据 */
	public rootMaps: Array<MapInfo> = []

	public Events: Record<string, Array<EventBase>> = {}

	/** 兼容旧业务：返回所有剧情的平铺列表。 */
	public get Maps(): Array<MapInfo> {
		return this.mapList
	}

	public set Maps(value: Array<MapInfo>) {
		this.createStoryTree(value)
	}

	/// <summary>
	/// 创建普通的实例
	/// 可能只有新建的时候会用
	/// </summary>
	constructor(_reader?: FileReader) {
		if (_reader) {
			this.readBin(_reader)
		} else {
			this.ProjectName = ''
			this.SelectMapIndex = 1
			const map = new MapInfo()
			this.createStoryTree([map])
		}
	}

	/**
	 * 根据 `Maps` 平铺列表里每条剧情的 `Id`、`ParentId` 构建树形父子关系（写入各 `MapInfo.Childs`）。
	 * 根节点：无有效父节点（ParentId≤0、自指、或父 id 不在本工程场景列表中）。
	 */
	createStoryTree(maps: Array<MapInfo> = this.mapList): void {
		const nextMaps = [...maps]
		this.mapList = nextMaps

		for (const map of nextMaps) {
			const key = map.Id.toString()
			const bucket = ensureEventListInstances(this.Events[key] ?? map.List ?? [])
			this.Events[key] = bucket
			map.List = bucket
		}

		for (const m of maps) {
			m.Childs = []
		}
		if (nextMaps.length === 0) {
			this.rootMaps = []
			return
		}

		const byId = new Map<number, MapInfo>()
		for (const m of nextMaps) {
			byId.set(m.Id, m)
		}
		const allIds = new Set(nextMaps.map((x) => x.Id))

		for (const map of nextMaps) {
			const pid = map.ParentId
			const isRoot =
				pid <= 0 ||
				pid === map.Id ||
				!allIds.has(pid)
			if (isRoot) continue
			const parent = byId.get(pid)
			if (parent) {
				parent.Childs.push(map)
			}
		}

		for (const m of nextMaps) {
			m.Childs.sort((a, b) => {
				if (a.Order !== b.Order) return a.Order - b.Order
				return a.Id - b.Id
			})
		}

		this.rootMaps = nextMaps
			.filter((map) => {
				const pid = map.ParentId
				return pid <= 0 || pid === map.Id || !allIds.has(pid)
			})
			.sort((a, b) => {
				if (a.Order !== b.Order) return a.Order - b.Order
				return a.Id - b.Id
			})
	}

	readBin(reader: FileReader, ctx?: StoryTreeReadContext): void {
		this.ProjectName = reader.readString()
		this.SelectMapIndex = reader.readInt()
		this.Id = reader.readInt()
		const maps: Array<MapInfo> = new Array()
		this.Events = {}
		const length = reader.readInt()
		for (let i = 0; i < length; i++) {
			let mapInfo = new MapInfo(reader)
			maps.push(mapInfo)
			this.Events[mapInfo.Id.toString()] = ensureEventListInstances(mapInfo.List)
		}
		this.createStoryTree(maps)
	}

	/**
	 * StoryTree 模块体（mid=20）：仅元数据到 List.length，不含 MapInfo。
	 * MapInfo 由 MainProject 按 `10000000+Id` 单独写入 DataMap。
	 */
	writeBinHeader(writer: FileWriter): void {
		writer.writeString(this.ProjectName)
		writer.writeInt(this.SelectMapIndex)
		writer.writeInt(this.Id)
		writer.writeInt(this.getMapCount(this.rootMaps))
	}

	/** 按树序写出各 MapInfo（须紧接在 writeBinHeader 之后，以兼容当前顺序 readBin）。 */
	writeMapBins(writer: FileWriter): void {
		this.writeMap(writer, this.rootMaps)
	}

	/** 内联写出（测试/旧路径）；工程保存请走 MainProject.writeBinBuffer。 */
	writeBin(writer: FileWriter): void {
		this.writeBinHeader(writer)
		this.writeMapBins(writer)
	}

	getMapCount(maps: Array<MapInfo>): number {
		var count = maps.length;
		for(let i = 0; i < maps.length; i++)
		{
			count += this.getMapCount(maps[i]!.Childs)
		}
		return count;
	}

	writeMap(writer: FileWriter, maps: Array<MapInfo>): void {
		for(let i = 0; i < maps.length; i++)
		{
			const map = maps[i]!
			map.List = this.loadEvent(map.Id)
			map.writeBin(writer)
			this.writeMap(writer, map.Childs!)
		}
	}

	loadEvent(index: number): Array<EventBase> {
		const key = index.toString()
		const map = this.mapList.find((item) => item.Id === index) ?? null
		const bucket = ensureEventListInstances(this.Events[key] ?? map?.List ?? [])
		this.Events[key] = bucket
		if (map) {
			map.List = bucket
		}
		return bucket
	}

	writeGameBin(writer: FileWriter): void {
		const maps = Array.isArray(this.Maps) ? this.Maps : []
		writer.writerIntLength()
		writer.writeString(normalizeString(this.ProjectName))
		writer.writeInt(maps.length)
		for (let i = 0; i < maps.length; i++) {
			const map = maps[i]
			map.List = this.loadEvent(map.Id)
			map.writeGameBin(writer)
		}
		writer.writerLength()
	}
}

export default StoryTree